import React, { RefObject } from 'react';
import { MenuItem, MenuItemProps } from '@mui/material';
import Autocomplete, { AutocompleteProps } from '../Form/Autocomplete';
import { uniqBy } from 'lodash';
import { selectAssignableTaskGroups } from '@/redux/selectors/constantsSelectors';
import GroupDisplay from './GroupDisplay';
import styled from '@emotion/styled';
import { UserGroup } from '../../../redux/slices/slice_types';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { useAppSelector } from '@/redux/hooks';

export type GroupSearchProps = {
  onSelect: AutocompleteProps['onChange'];
  value?: string;
  placeholder?: string;
  innerRef?: RefObject<any> | null;
  isTableFilter?: boolean;
};

const GroupSearch = ({ onSelect, value, placeholder = 'No groups found', innerRef, isTableFilter, }: GroupSearchProps) => {
  const assignableTaskGroups = useAppSelector(selectAssignableTaskGroups);

  const renderOption = (props: any, option: any) => {
    const newProps = {
      ...props,
      key: option.id,
      className: props.className
    };

    if (!option) return '';
    return (
      <GroupOption key={option.id} role="option" {...newProps}>
        <GroupDisplay group={option} iconSize={14} />
      </GroupOption>
    );
  };

  const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: (option: UserGroup) => option.group
  });

  let options = [...assignableTaskGroups];
  if (value && typeof value !== 'string') options.push(value);
  options = uniqBy(options, 'id');
  value && options.unshift({ id: 0, group: 'Unassigned' });

  return (
    <Autocomplete
      size={isTableFilter ? 'large' : ('small' as any)}
      label={isTableFilter ? 'Quick Search Group' : ''}
      options={options}
      value={value || null}
      sx={{ width: 190 }}
      innerRef={innerRef as RefObject<any>}
      getOptionLabel={(option: any) => (option?.first_name && option?.last_name ? `${option.first_name} ${option.last_name}` : '')}
      noOptionsText="No groups found."
      openOnFocus={false}
      onChange={onSelect}
      placeholder={placeholder}
      blurOnSelect
      renderOption={renderOption}
      inputProps={{ hiddenLabel: true }}
      isOptionEqualToValue={(option: UserGroup, value: UserGroup) => option?.id === value?.id}
      variant={isTableFilter ? 'outlined' : 'filled'}
      filterOptions={filterOptions}
    />
  );
};

export default GroupSearch;

const GroupOption = styled(MenuItem)`
  width: 100%;
  overflow: hidden;
  flex-direction: column;

  & svg {
    min-width: 15px;
  }

  &.MuiMenuItem-root {
    padding: ${({ theme }) => theme.spacing(1.5, 0.5, 1.5, 1.5)};
    display: flex;
    align-items: flex-start;
    font-size: 0.9rem;
    white-space: normal;
    word-break: break-word;
    line-height: 1.2;
  }
`;
